import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import classNames from 'classnames';
import { defaultTo, isFunction } from 'lodash-es';
import { CaretDown, CaretUp } from 'phosphor-react';
import type { JSX, PropsWithChildren } from 'react';
import { useId, useMemo } from 'react';
import { CheckboxSurrogate } from '../../checkbox/checkbox';
import type { FormFieldVariant } from '../form-field-variant';
import { useOptionContainerWidthSync } from '../option-container-viewport-aware-resize';
import { useMenuOpenState } from './menu-open';
import type { Option, OptionDecorator, OptionValue } from './option';
import { DefaultOptionDecorator, toOptions } from './option';
import { OptionsFilter, useOptionsFilter } from './options-filter';
import { useValues } from './value';

type MultiSelectProps = {
  className?: string;
  readOnly?: boolean;
  disabled?: boolean;
  displayOptionsFilter?: boolean;
  options: string[] | Option[] | Record<string, string>;
  variant?: FormFieldVariant;
  optionDecorator?: OptionDecorator;
  placeholder?: string | (() => JSX.Element);
  values: OptionValue[];
  onValueChange: (value: OptionValue[]) => void;
};

/**
 * @deprecated use Listbox instead
 */
export const MultiSelect = ({
  className,
  readOnly = false,
  disabled = readOnly,
  displayOptionsFilter = false,
  onValueChange,
  options: optionsOrValues,
  optionDecorator = DefaultOptionDecorator,
  placeholder: placeholderFn = 'Bitte wählen',
  values,
  variant = 'default',
}: MultiSelectProps) => {
  const { triggerRef, contentStyle } = useOptionContainerWidthSync<HTMLButtonElement>();
  const { closeMenu, menuOpen, rootProps } = useMenuOpenState(disabled);
  const { selectValue, selectedValues, deselectValue, toggleValue, isSelectedValue, hasSelectedValues } = useValues(values, onValueChange);

  const options = useMemo(() => toOptions(optionsOrValues), [optionsOrValues]);
  const { optionsFilterProps, contentRef, filteredOptions } = useOptionsFilter(options, menuOpen, toggleValue, closeMenu, false, false);

  const placeholder = isFunction(placeholderFn) ? placeholderFn() : placeholderFn;
  const label = hasSelectedValues ? options.filter(o => selectedValues.includes(o.value)).map(o => o.label)
    .join(', ') : placeholder;
  const isPlaceholder = label === placeholder;

  return (
    <DropdownMenu.Root {...rootProps}>
      <div className="tds-input-dropdown-wrapper">
        <DropdownMenu.Trigger
          disabled={disabled}
          ref={triggerRef}
          className={classNames('flex justify-between items-center tds-input-field', className, {
            'tds-input-field-default': variant === 'default',
            'tds-input-field-small': variant === 'small',
            'select-none': !readOnly,
            'bg-contrast text-dark': !disabled,
            'cursor-default': disabled && !readOnly,
            'bg-transparent px-0 cursor-default': readOnly,
          })}
        >
          <span className={classNames('flex-1 text-left tds-input-value whitespace-nowrap overflow-clip', {
            'tds-input-value-default': variant === 'default',
            'tds-input-value-small': variant === 'small',
          })}>
            <span className={classNames({ 'tds-input-value-placeholder': isPlaceholder })}>{label}</span>
          </span>
          {menuOpen ? <CaretUp size={16} className="ml-200 tds-input-icon" /> : <CaretDown size={16} className={classNames('ml-200 tds-input-icon', { disabled })} />}
        </DropdownMenu.Trigger>

        <DropdownMenu.Content
          align="start"
          style={contentStyle}
          ref={contentRef}
          className={classNames('mt-2 select-none tds-option-container tds-input-value', {
            'tds-input-value-default': variant === 'default',
            'tds-input-value-small': variant === 'small',
          })}
        >
          {displayOptionsFilter ? <OptionsFilter {...optionsFilterProps} variant={variant}/> : null}
          <DropdownMenu.DropdownMenuGroup className={classNames('overflow-y-auto max-h-96')}>
            {filteredOptions.map(option => (
              <MenuEntry
                key={defaultTo(option.value, 'undefined')}
                className="flex items-center justify-start select-none tds-option-container-item"
                // we have no empty option in multiselect, so option.value is never undefined
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                checked={isSelectedValue(option.value!)}
                // we have no empty option in multiselect, so option.value is never undefined
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                onCheckedChange={isChecked => isChecked ? selectValue(option.value!) : deselectValue(option.value!)}
              >
                {optionDecorator(option)}
              </MenuEntry>
            ))}
          </DropdownMenu.DropdownMenuGroup>
        </DropdownMenu.Content>
      </div>
    </DropdownMenu.Root >
  );
};

type MenuEntryProps = {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  className?: string;
};

const MenuEntry = ({ children, className, checked, onCheckedChange }: PropsWithChildren<MenuEntryProps>) => {
  const id = useId();

  return (
    <DropdownMenu.CheckboxItem
      className={className}
      checked={checked}
      onCheckedChange={onCheckedChange}
      onSelect={event => event.preventDefault()}
    >
      <>
        <CheckboxSurrogate className="mr-100" checked={checked} />
        <label htmlFor={id}>
          {children}
        </label>
      </>
    </DropdownMenu.CheckboxItem>
  );
};
